/**
 * PostHog capture rules: where the browser SDK may run and what it may send.
 *
 * Pure constant module (no env reads beyond the two public flags below, no
 * SDK import) so the client instrumentation, the identity component and the
 * tests share one definition.
 *
 * Founder decisions, 2026-10-13:
 * - Products: product analytics, session replay, error tracking. No logs.
 * - Signed-in people are identified by internal IDs only (auth user id,
 *   person id, organization account id). No email, name or org name.
 * - Signed-out pages are captured cookieless: no cookie, no stored id, and
 *   therefore no replay (the SDK refuses to record in cookieless mode).
 *   Every page outside the signed-in apps is treated this way, whoever is
 *   looking at it.
 * - Replay records the screen unmasked. Credential-like inputs stay masked
 *   (`isCredentialLikeInput`), because a typed secret is not screen content.
 *   Event properties are a different matter: they carry ids, never the text
 *   or link target of what was clicked, and never a page title.
 *
 * Rules that follow from the product invariants rather than a choice:
 * - No-login token links never reach PostHog. Their URL is the credential.
 * - Nothing is captured while a platform admin is viewing as or swapped
 *   into another account.
 */

import { PORTAL_HOSTS, LOGIN_ROUTER_HOST } from "@/lib/auth/portal-hosts";

/** Public project token ("Default project", 662685, US cloud). Write-only. */
export const POSTHOG_PROJECT_TOKEN =
  "phc_txfmbMtKjtGKhojZZKDnBdGvBNwRZeqmvvGDkwQp3ZMk";

/** Ingestion host. `NEXT_PUBLIC_POSTHOG_HOST` swaps in a reverse proxy. */
export const POSTHOG_API_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

/** The PostHog app, for links the SDK builds when a proxy fronts ingestion. */
export const POSTHOG_UI_HOST = "https://us.posthog.com";

/** localStorage key holding the in/out choice; cleared when signed out. */
export const POSTHOG_CONSENT_STORAGE_KEY = "vs_ph_capture";

/** Hosts that serve only a portal, at the host root (`next.config.js`). */
const PORTAL_ROOT_HOSTS: ReadonlySet<string> = new Set([
  PORTAL_HOSTS.virtualspace,
  PORTAL_HOSTS.spatialist,
  LOGIN_ROUTER_HOST,
]);

/** Production hosts the SDK runs on. Previews and localhost are not here. */
const CAPTURE_HOSTS: ReadonlySet<string> = new Set([
  ...PORTAL_ROOT_HOSTS,
  PORTAL_HOSTS.spacestation,
  // Get Started and Services, including the marketing-site iframes.
  "explore.virtualspace.xyz",
]);

const LOCAL_HOSTS: ReadonlySet<string> = new Set(["localhost", "127.0.0.1"]);

/**
 * First path segments that bypass the portal-host rewrite and are served
 * from the app root instead. All of them are token links, auth callbacks or
 * handlers. Keep in step with `portalHostRewrites` in `next.config.js`.
 */
const PORTAL_HOST_PASSTHROUGH_SEGMENTS: ReadonlySet<string> = new Set([
  "api",
  "_next",
  "f",
  "survey",
  "upload",
  "auth",
  "c",
  "connect",
]);

/**
 * Portal pages whose path carries a one-time token. A test scans
 * `src/app/portal` for token-named segments and fails when one is missing.
 */
const PORTAL_TOKEN_SEGMENTS: ReadonlySet<string> = new Set([
  "call",
  "confirm-email",
]);

/** The signed-in apps as path prefixes, on hosts that serve the app root. */
const PREFIXED_PORTAL_PATH = /^\/portal\/(?:vs|go)(?:\/([^/]*))?/;

/**
 * First path segments captured on the non-portal hosts. An allowlist, so a
 * new token route is left out until someone adds it here on purpose.
 */
const CAPTURABLE_ROOT_SEGMENTS: ReadonlySet<string> = new Set([
  "",
  "dashboard",
  "portal",
  "login",
  "signin",
  "router",
  "get-started",
  "services",
  "coverage",
  "join",
  "legal",
  "security",
  "faq",
  "industries",
  "solutions",
  "spatialists",
]);

function bareHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, "");
}

function firstSegment(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

/** Localhost capture is opt-in, for verifying the wiring by hand. */
export function isLocalCaptureEnabled(): boolean {
  return process.env.NEXT_PUBLIC_POSTHOG_LOCAL_CAPTURE === "1";
}

/** True when the SDK may load on this host at all. */
export function isCaptureHost(hostname: string): boolean {
  const host = bareHost(hostname);
  if (CAPTURE_HOSTS.has(host)) return true;
  return LOCAL_HOSTS.has(host) && isLocalCaptureEnabled();
}

/**
 * The path half of `isCapturablePage`: may this path be named in an event
 * sent from `hostname`? False for every no-login token link, auth callback
 * and file route.
 */
function isCapturablePath(hostname: string, rawPathname: string): boolean {
  // Match what the router matches: an encoded segment names the same page.
  let pathname = rawPathname;
  try {
    pathname = decodeURIComponent(rawPathname);
  } catch {
    return false;
  }
  const segment = firstSegment(pathname);
  if (PORTAL_ROOT_HOSTS.has(bareHost(hostname))) {
    if (PORTAL_HOST_PASSTHROUGH_SEGMENTS.has(segment)) return false;
    if (PORTAL_TOKEN_SEGMENTS.has(segment)) return false;
    // The portal rewrite skips any path with a dot, so the app root serves
    // it: possibly a token link, never a portal page.
    if (pathname.includes(".")) return false;
    // `/notifications` is the portal's own settings page; `/manage` and
    // `/station` under it are no-login links from email.
    return !/^\/notifications\/(?:manage|station)(?:\/|$)/.test(pathname);
  }
  const portal = PREFIXED_PORTAL_PATH.exec(pathname);
  if (portal && PORTAL_TOKEN_SEGMENTS.has(portal[1] ?? "")) return false;
  return CAPTURABLE_ROOT_SEGMENTS.has(segment);
}

/** True when events from this page may be sent. */
export function isCapturablePage(hostname: string, pathname: string): boolean {
  return isCaptureHost(hostname) && isCapturablePath(hostname, pathname);
}

/**
 * True for a capturable page inside a signed-in app shell. Those layouts
 * render `AnalyticsIdentity`, and the SDK waits for its verdict (identify,
 * signed out, or impersonating) instead of starting on its own.
 */
export function awaitsIdentity(hostname: string, pathname: string): boolean {
  if (!isCapturablePage(hostname, pathname)) return false;
  const host = bareHost(hostname);
  if (host === PORTAL_HOSTS.virtualspace || host === PORTAL_HOSTS.spatialist) {
    return true;
  }
  if (host === LOGIN_ROUTER_HOST) return false;
  const segment = firstSegment(pathname);
  return segment === "dashboard" || segment === "portal";
}

/** Query keys whose values are navigation state, not data. All else is blanked. */
const KEPT_QUERY_KEYS: ReadonlySet<string> = new Set([
  "view",
  "section",
  "tab",
  "step",
  "embed",
  "lang",
  "utm_source",
  "utm_medium",
  "utm_campaign",
]);

const REDACTED_PATH = "/[redacted]";

/** Hosts whose paths this app serves, so its token rules apply to them. */
function isAppHost(hostname: string): boolean {
  const host = bareHost(hostname);
  return CAPTURE_HOSTS.has(host) || LOCAL_HOSTS.has(host);
}

/**
 * Make a URL or path safe to send from `currentHostname`:
 * - the fragment is dropped (Supabase and OAuth callbacks put tokens there);
 * - every query value is blanked except navigation state (`KEPT_QUERY_KEYS`),
 *   so an email, token, code or `next=` target never rides along;
 * - a path on one of this app's hosts that may not be captured (a token
 *   link the visitor came from, say) is replaced whole.
 */
export function scrubUrl(value: string, currentHostname: string): string {
  const hashAt = value.indexOf("#");
  const withoutHash = hashAt === -1 ? value : value.slice(0, hashAt);
  const queryAt = withoutHash.indexOf("?");
  let location = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);

  const absolute = /^([a-z][a-z0-9+.-]*:)?\/\/([^/]*)(\/.*)?$/i.exec(location);
  const host = absolute ? absolute[2].replace(/^.*@/, "").replace(/:\d+$/, "") : currentHostname;
  const path = absolute ? (absolute[3] ?? "/") : location;
  if (path.startsWith("/") && isAppHost(host) && !isCapturablePath(host, path)) {
    const origin = absolute
      ? location.slice(0, location.length - (absolute[3]?.length ?? 0))
      : "";
    location = `${origin}${REDACTED_PATH}`;
  }
  if (queryAt === -1) return location;

  const query = withoutHash
    .slice(queryAt + 1)
    .split("&")
    .filter((pair) => pair !== "")
    .map((pair) => {
      const equalsAt = pair.indexOf("=");
      // A bare `?value` has no key to keep: it is all value.
      if (equalsAt === -1) return "redacted";
      const key = pair.slice(0, equalsAt);
      return KEPT_QUERY_KEYS.has(key.toLowerCase()) ? pair : `${key}=redacted`;
    })
    .join("&");
  return query ? `${location}?${query}` : location;
}

const URL_PROPERTY_KEY = /url|referrer|href|pathname/i;

const MAX_SCRUB_DEPTH = 4;

/**
 * Scrub every URL- or path-shaped string property of an event, in place,
 * including ones the SDK nests a few levels down. Replay payloads are left
 * alone: a recording is the page as shown.
 */
export function scrubUrlProperties(
  properties: Record<string, unknown> | null | undefined,
  currentHostname: string,
  depth = 0,
): void {
  if (!properties) return;
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === "string") {
      if (URL_PROPERTY_KEY.test(key)) {
        properties[key] = scrubUrl(value, currentHostname);
      }
    } else if (
      value &&
      typeof value === "object" &&
      depth < MAX_SCRUB_DEPTH &&
      !key.startsWith("$snapshot")
    ) {
      for (const child of Array.isArray(value) ? value : [value]) {
        if (child && typeof child === "object") {
          scrubUrlProperties(
            child as Record<string, unknown>,
            currentHostname,
            depth + 1,
          );
        }
      }
    }
  }
}

type OutgoingEvent = {
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
};

/**
 * The last gate before an event leaves the browser. Returns null to drop it:
 * while capture is suppressed (impersonation) and on any page that may not
 * be captured, which covers a client-side navigation into a token link.
 */
export function sanitizeEvent<T extends OutgoingEvent>(
  event: T | null,
  page: { suppressed: boolean; hostname: string; pathname: string },
): T | null {
  if (!event || page.suppressed) return null;
  if (!isCapturablePage(page.hostname, page.pathname)) return null;
  scrubUrlProperties(event.properties, page.hostname);
  scrubUrlProperties(event.$set, page.hostname);
  scrubUrlProperties(event.$set_once, page.hostname);
  // A page title is content: a mission's is its location name.
  if (event.properties) delete event.properties.title;
  return event;
}

const CREDENTIAL_HINT =
  /pass(?:word|code)?|secret|token|api[-_ ]?key|otp|one[-_ ]?time|verification|credential|cvc|cvv|card/i;

/**
 * True for inputs whose typed value is a credential, not screen content.
 * Replay masks these even though the rest of the screen is recorded.
 */
export function isCredentialLikeInput(attrs: {
  type?: string | null;
  name?: string | null;
  id?: string | null;
  autocomplete?: string | null;
}): boolean {
  if ((attrs.type ?? "").toLowerCase() === "password") return true;
  return [attrs.name, attrs.id, attrs.autocomplete].some(
    (hint) => typeof hint === "string" && CREDENTIAL_HINT.test(hint),
  );
}
